import React from 'react';
import { FiSearch } from 'react-icons/fi';
import { useI18n } from '../utilities/i18n';
import {
  PLAYER_POSITIONS,
  getPlayerPhotoUrl,
  getTeamPhotoUrl,
  playerDisplayName,
  playerPositionLabel,
  uniquePlayerTeams,
} from '../utilities/playersApi';
import { applicationOptionLabel } from '../utilities/tournamentsApi';
import AdminPhotoUpload from './AdminPhotoUpload';
import defaultPlayerImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';

function ExistingPlayerCard({ item }) {
  const teams = uniquePlayerTeams(item?.teams);
  return (
    <span className="pl-existing-info">
      <img
        src={getPlayerPhotoUrl(item?.image || item?.photo || item?.avatar) || defaultPlayerImage}
        alt=""
        className="pm-list-photo"
        onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
      />
      <span className="pl-existing-meta">
        <span className="pl-existing-name">{playerDisplayName(item) || '—'}</span>
        <span className="pl-existing-sub">
          {playerPositionLabel(item) || '—'}
        </span>
        {teams.length ? (
          <span className="pm-list-teams">
            {teams.map((team) => (
              <span key={team.team_id || team.id || team.team_name} className="pm-list-team">
                <img
                  src={getTeamPhotoUrl(team.team_image || team.image) || defaultTeamLogo}
                  alt=""
                  onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                />
                <span>{team.team_name || team.name || '—'}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </span>
  );
}

export default function PlayerFormFields({
  form,
  errors,
  photoPreview,
  photoError,
  saving,
  photoRequired,
  showTeamBinding,
  applications = [],
  teamName = '',
  onFieldChange,
  onPhotoChange,
  showExistingCheck = false,
  canCheckExisting = false,
  checkingExisting = false,
  existingMatches = null,
  selectedExisting = null,
  onCheckExisting,
  onSelectExisting,
  onClearExisting,
}) {
  const { t } = useI18n();

  const applicationFields = showTeamBinding ? (
    <>
      <div className="st-field">
        <label>{t('apaApplication')} <span className="st-required">*</span></label>
        <select
          className={`st-select ${errors.application_id ? 'error' : ''}`}
          value={form.application_id}
          disabled={saving || !applications.length}
          onChange={(event) => onFieldChange('application_id', event.target.value)}
        >
          <option value="">{applications.length ? t('apaPickApp') : t('apaNoApps')}</option>
          {applications.map((app) => {
            const id = app.application_id || app.id;
            return (
              <option key={id} value={id}>
                {applicationOptionLabel(app) || t('mtAppFallback', { id })}
              </option>
            );
          })}
        </select>
        {errors.application_id ? <span className="st-field-error">{errors.application_id}</span> : null}
      </div>

      <div className="st-field">
        <label>{t('pdNumber')}</label>
        <input
          type="number"
          min="0"
          className={`st-input ${errors.number ? 'error' : ''}`}
          value={form.number}
          placeholder={t('apaNumberPh')}
          onChange={(event) => onFieldChange('number', event.target.value)}
        />
        {errors.number ? <span className="st-field-error">{errors.number}</span> : null}
      </div>
    </>
  ) : null;

  return (
    <div className="tr-league-form pl-form">
      {showTeamBinding && teamName ? (
        <div className="pl-team-note">
          {t('plAddingToTeam', { team: teamName })}
        </div>
      ) : null}

      {selectedExisting ? (
        <div className="pl-existing-picked">
          <ExistingPlayerCard item={selectedExisting} />
          <button
            type="button"
            className="st-btn st-btn-secondary"
            disabled={saving}
            onClick={onClearExisting}
          >
            {t('plClearExisting')}
          </button>
        </div>
      ) : (
        <>
          <div className="tr-curator-main">
            <div className="st-field tr-curator-photo">
              <label>
                {t('epmPhoto')}
                {photoRequired ? <span className="st-required"> *</span> : null}
              </label>
              <AdminPhotoUpload
                preview={photoPreview}
                error={photoError}
                disabled={saving}
                onFileChange={onPhotoChange}
              />
            </div>

            <div className="tm-edit-names">
              <div className="st-field">
                <label>{t('epmFamily')} <span className="st-required">*</span></label>
                <input
                  className={`st-input ${errors.family ? 'error' : ''}`}
                  value={form.family}
                  placeholder={t('epmFamilyPh')}
                  onChange={(event) => onFieldChange('family', event.target.value)}
                />
                {errors.family ? <span className="st-field-error">{errors.family}</span> : null}
              </div>

              <div className="st-field">
                <label>{t('epmName')} <span className="st-required">*</span></label>
                <input
                  className={`st-input ${errors.name ? 'error' : ''}`}
                  value={form.name}
                  placeholder={t('epmNamePh')}
                  onChange={(event) => onFieldChange('name', event.target.value)}
                />
                {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
              </div>

              <div className="st-field">
                <label>{t('epmFather')}</label>
                <input
                  className="st-input"
                  value={form.father_name}
                  placeholder={t('epmFatherPh')}
                  onChange={(event) => onFieldChange('father_name', event.target.value)}
                />
              </div>

              {showExistingCheck ? (
                <button
                  type="button"
                  className="st-btn st-btn-secondary pl-check-btn"
                  disabled={!canCheckExisting || checkingExisting || saving}
                  onClick={onCheckExisting}
                >
                  <FiSearch /> {checkingExisting ? t('plCheckingExists') : t('plCheckExists')}
                </button>
              ) : null}
            </div>
          </div>

          {showExistingCheck && existingMatches ? (
            <div className="pl-existing-list">
              {existingMatches.length ? (
                <>
                  <div className="pl-existing-list-title">{t('plExistsFound', { count: existingMatches.length })}</div>
                  {existingMatches.map((item, index) => (
                    <div key={item.id || item.player_id || index} className="pl-existing-row">
                      <ExistingPlayerCard item={item} />
                      <button
                        type="button"
                        className="st-btn st-btn-primary"
                        disabled={saving}
                        onClick={() => onSelectExisting(item)}
                      >
                        {t('plSelectExisting')}
                      </button>
                    </div>
                  ))}
                </>
              ) : (
                <div className="pl-existing-empty">{t('plExistsEmpty')}</div>
              )}
            </div>
          ) : null}

          <div className="st-form-grid pl-form-grid">
            <div className="st-field">
              <label>{t('pmPosition')}</label>
              <select
                className="st-select"
                value={form.position}
                onChange={(event) => onFieldChange('position', event.target.value)}
              >
                {PLAYER_POSITIONS.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>

            <div className="st-field">
              <label>{t('plGender')}</label>
              <select
                className="st-select"
                value={form.gender}
                onChange={(event) => onFieldChange('gender', event.target.value)}
              >
                <option value="1">{t('plGenderMale')}</option>
                <option value="0">{t('plGenderFemale')}</option>
              </select>
            </div>

            <div className="st-field">
              <label>{t('plStatus')}</label>
              <select
                className="st-select"
                value={form.isProfessional}
                onChange={(event) => onFieldChange('isProfessional', event.target.value)}
              >
                <option value="0">{t('plStatusAmateur')}</option>
                <option value="1">{t('plStatusPro')}</option>
              </select>
            </div>

            <div className="st-field">
              <label>{t('pmBirthday')} <span className="st-required">*</span></label>
              <div className="st-native-dt">
                <input
                  type="date"
                  className={`st-input ${errors.birthday ? 'error' : ''}`}
                  value={form.birthday}
                  onChange={(event) => onFieldChange('birthday', event.target.value)}
                />
              </div>
              {errors.birthday ? <span className="st-field-error">{errors.birthday}</span> : null}
            </div>

            <div className="st-field">
              <label className="st-check">
                <input
                  type="checkbox"
                  checked={Boolean(form.isInsured)}
                  disabled={saving}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    onFieldChange('isInsured', checked);
                    if (!checked) onFieldChange('insuranceUntil', '');
                  }}
                />
                {t('plInsured')}
              </label>
            </div>

            <div className="st-field">
              <label>{t('plInsuranceUntil')}</label>
              <div className="st-native-dt">
                <input
                  type="date"
                  className="st-input"
                  value={form.insuranceUntil || ''}
                  disabled={saving || !form.isInsured}
                  onChange={(event) => onFieldChange('insuranceUntil', event.target.value)}
                />
              </div>
              <span className="st-photo-hint">{t('plInsuranceUntilHint')}</span>
            </div>

            <div className="st-field">
              <label>{t('epmHeight')}</label>
              <input
                type="number"
                min="0"
                className="st-input"
                value={form.height}
                onChange={(event) => onFieldChange('height', event.target.value)}
              />
            </div>

            <div className="st-field">
              <label>{t('epmWeight')}</label>
              <input
                type="number"
                min="0"
                className="st-input"
                value={form.weight}
                onChange={(event) => onFieldChange('weight', event.target.value)}
              />
            </div>

            <div className="st-field st-field-wide">
              <label>{t('plCitizenship')}</label>
              <input
                className="st-input"
                value={form.nationality}
                placeholder={t('epmNationalityPh')}
                onChange={(event) => onFieldChange('nationality', event.target.value)}
              />
            </div>
          </div>
        </>
      )}

      {applicationFields ? (
        <div className="st-form-grid pl-form-grid pl-app-only-grid">
          {applicationFields}
        </div>
      ) : null}
    </div>
  );
}
